/*
 * sm-pdp.js — wiring for the Smarteez PDP Info block (blocks/sm-pdp-info.liquid)
 *
 * Responsibilities (kept narrow — global ATC form interception lives in
 * sm-card-product.js and we lean on that):
 *
 *   1. Quantity +/- buttons (inline row + mobile sticky bar mirror)
 *   2. Mobile sticky bar visibility (IntersectionObserver on buy row)
 *   3. Buy Now submit -> /cart/add then /checkout redirect
 *   4. Variant-change subscription (Dawn PUB_SUB_EVENTS) to keep
 *      availability state + hurry line + price in sync
 *   5. Media gallery thumbnail-swap (clicking a thumb swaps the main
 *      image src using the metadata serialised on the wrapper)
 */
(function () {
  // Per-root binding is tracked via `root.__smPdpBound` (see initBlock).
  // We intentionally do NOT short-circuit at the IIFE level — theme-editor
  // `shopify:section:load` / `shopify:section:unload` events fire on every
  // section reload and we need a fresh init each time.

  var MOBILE_QUERY = '(max-width: 989px)';

  // Track every IntersectionObserver created so we can disconnect them on
  // `shopify:section:unload` (theme editor) and avoid leaks across reloads.
  var smPdpObservers = (window.__smPdpObservers = window.__smPdpObservers || new Set());

  // Per-handle pill-button label for the Avis personalisation popup.
  // Keeps the legacy hardcoded list because the body class isn't enough —
  // we want the right copy for each personalised SKU.
  var BUNDLE_TEXT_MAP = {
    'smarteez-personalised-slime-jar': 'Build Your Own Custom Jar Now',
    'smarteez-personalised-slime-kit-blue-celebration': 'Build Your Own Custom Kit Now',
  };

  function formatMoney(cents) {
    if (typeof cents !== 'number') cents = parseInt(cents, 10);
    if (Number.isNaN(cents)) return '';
    // Shopify INR locale uses no decimals at the storefront — we follow
    // the same convention to keep the swap visually identical to the
    // server-rendered initial price.
    var symbol = '₹';
    var amount = (cents / 100);
    // Render as integer when fractional part is zero, otherwise 2dp.
    if (amount === Math.floor(amount)) return symbol + amount.toFixed(0);
    return symbol + amount.toFixed(2);
  }

  function clamp(value, min, max) {
    if (Number.isNaN(value)) return min;
    if (max != null && value > max) return max;
    if (value < min) return min;
    return value;
  }

  function dispatchChange(input) {
    input.dispatchEvent(new Event('input', { bubbles: true }));
    input.dispatchEvent(new Event('change', { bubbles: true }));
  }

  function initBlock(root) {
    if (root.__smPdpBound) return;
    root.__smPdpBound = true;

    var qtyInput = root.querySelector('[data-sm-pdp-qty-input]');
    var qtyMinus = root.querySelector('[data-sm-pdp-qty-minus]');
    var qtyPlus = root.querySelector('[data-sm-pdp-qty-plus]');
    var form = root.querySelector('[data-sm-pdp-form]');
    var atcBtn = root.querySelector('[data-sm-pdp-atc]');
    var atcLabel = root.querySelector('[data-sm-pdp-atc-label]');
    var buyNowBtn = root.querySelector('[data-sm-pdp-buy-now]');
    var buyNowLabel = root.querySelector('[data-sm-pdp-buy-now-label]');
    var variantInput = root.querySelector('[data-sm-pdp-variant-id]');
    var oosMsg = root.querySelector('[data-sm-pdp-oos]');
    var hurryMsg = root.querySelector('[data-sm-pdp-hurry]');
    var hurryQty = root.querySelector('[data-sm-pdp-hurry-qty]');
    var stickyBar = root.querySelector('[data-sm-pdp-sticky-bar]');
    var stickyMinus = root.querySelector('[data-sm-pdp-sticky-minus]');
    var stickyPlus = root.querySelector('[data-sm-pdp-sticky-plus]');
    var stickyQtyVal = root.querySelector('[data-sm-pdp-sticky-qty-val]');
    var stickyAtc = root.querySelector('[data-sm-pdp-sticky-atc]');
    var stickyAtcLabel = root.querySelector('[data-sm-pdp-sticky-atc-label]');
    var stickyBuyNow = root.querySelector('[data-sm-pdp-sticky-buy-now]');
    var buyRow = root.querySelector('.sm-pdp__cta-row') || root.querySelector('.sm-pdp__buy-row') || root.querySelector('.sm-pdp__buy-row--personalised');
    var stickyAvis = root.querySelector('[data-sm-pdp-sticky-avis]');

    // Phones: Product Description accordion starts CLOSED. Server renders
    // it open (desktop default); closing here at init happens before first
    // paint since this script is deferred.
    if (window.matchMedia('(max-width: 749px)').matches) {
      var openAcc = root.querySelector('.sm-pdp__accordion details[open]');
      if (openAcc) openAcc.removeAttribute('open');
    }

    // Personalised SKU sticky bar: clicking the sticky button proxies a click
    // to the inline Avis-injected `.avis-btn-open-popup` so Avis opens its
    // popup. We use closest('sm-pdp-info') to scope the lookup to this block.
    if (stickyAvis) {
      stickyAvis.addEventListener('click', function () {
        var inlineAvis = root.querySelector('.avis-btn-open-popup');
        if (inlineAvis) inlineAvis.click();
      });
    }

    // Share button on product image. Prefer the Web Share API on supported
    // mobile browsers (it opens the OS share sheet). On desktop / browsers
    // without the share API, copy the URL to clipboard and flash a toast.
    var shareBtn = root.querySelector('[data-sm-pdp-share]');
    if (shareBtn) {
      function showShareToast() {
        shareBtn.classList.add('is-copied');
        setTimeout(function () { shareBtn.classList.remove('is-copied'); }, 1800);
      }
      shareBtn.addEventListener('click', function () {
        var titleEl = root.querySelector('.sm-pdp__title');
        var data = { title: titleEl ? titleEl.textContent.trim() : document.title, url: window.location.href };
        // Web Share API is fastest path on mobile; only call it on touch-capable
        // devices. Some desktop browsers expose navigator.share but it's not
        // reliable and the OS share sheet is missing — fall through to copy.
        var isTouchish = typeof navigator !== 'undefined'
          && (('ontouchstart' in window) || navigator.maxTouchPoints > 0);
        if (isTouchish && navigator.share) {
          navigator.share(data).catch(function () {
            if (navigator.clipboard) {
              navigator.clipboard.writeText(data.url).then(showShareToast).catch(function () {});
            }
          });
        } else if (navigator.clipboard) {
          navigator.clipboard.writeText(data.url).then(showShareToast).catch(function () {});
        }
      });
    }

    // Offer code chips — click to copy to clipboard with brief "Copied!" toast.
    root.querySelectorAll('.sm-pdp__offer-code').forEach(function (chip) {
      chip.setAttribute('role', 'button');
      chip.setAttribute('tabindex', '0');
      function copy() {
        var code = chip.textContent.trim();
        if (!code || !navigator.clipboard) return;
        navigator.clipboard.writeText(code).then(function () {
          chip.classList.add('is-copied');
          setTimeout(function () { chip.classList.remove('is-copied'); }, 1500);
        }).catch(function () {});
      }
      chip.addEventListener('click', copy);
      chip.addEventListener('keydown', function (e) {
        if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); copy(); }
      });
    });

    // Judge.me write-review modal autofill for logged-in customers.
    // Performance-conscious approach: NO observer runs on the idle page.
    // We attach a delegated click listener on the write-review button. When
    // clicked, we start a short-lived observer + poll for up to 60s, scoped
    // to a future modal subtree if we can find one (otherwise document.body
    // as a last resort). The observer auto-disconnects as soon as BOTH name
    // and email are filled, or after the 60s budget elapses.
    if (window.smPdpCustomer) {
      function fillJdgmInputs() {
        var done = true;
        var nameEl = document.getElementById('jdgm-name-input');
        var emailEl = document.getElementById('jdgm-email-input');
        if (nameEl && !nameEl.value && window.smPdpCustomer.name) {
          nameEl.value = window.smPdpCustomer.name;
          nameEl.dispatchEvent(new Event('input', { bubbles: true }));
        }
        if (emailEl && !emailEl.value && window.smPdpCustomer.email) {
          emailEl.value = window.smPdpCustomer.email;
          emailEl.dispatchEvent(new Event('input', { bubbles: true }));
        }
        // "Done" when both inputs exist and are populated (either by us or
        // already by the user) — stop observing.
        if (!nameEl || !emailEl) done = false;
        else if (!nameEl.value || !emailEl.value) done = false;
        return done;
      }
      function startJdgmObserver() {
        var obs = new MutationObserver(function () {
          if (fillJdgmInputs()) cleanup();
        });
        // Hard cap: 60 seconds, observer disconnects regardless.
        var budget = setTimeout(cleanup, 60000);
        function cleanup() {
          clearTimeout(budget);
          obs.disconnect();
          smPdpObservers.delete(obs);
        }
        obs.observe(document.body, { childList: true, subtree: true });
        smPdpObservers.add(obs);
        // Run once immediately in case the inputs are already in DOM.
        if (fillJdgmInputs()) cleanup();
      }
      document.addEventListener('click', function (e) {
        var t = e.target.closest('.jdgm-write-rev-link, .jdgm-rev-widg__write-button, [class*="jdgm-write"]');
        if (t) startJdgmObserver();
      });
    }

    var maxQty = 99;
    if (qtyInput && qtyInput.getAttribute('max')) {
      var parsed = parseInt(qtyInput.getAttribute('max'), 10);
      if (!Number.isNaN(parsed) && parsed > 0) maxQty = parsed;
    }

    function getQty() {
      var v = parseInt(qtyInput && qtyInput.value, 10);
      return clamp(v, 1, maxQty);
    }

    function setQty(next) {
      var clamped = clamp(parseInt(next, 10), 1, maxQty);
      if (qtyInput && parseInt(qtyInput.value, 10) !== clamped) {
        qtyInput.value = clamped;
        dispatchChange(qtyInput);
      }
      if (stickyQtyVal) stickyQtyVal.textContent = clamped;
    }

    // Quantity stepper
    if (qtyMinus) qtyMinus.addEventListener('click', function () { setQty(getQty() - 1); });
    if (qtyPlus) qtyPlus.addEventListener('click', function () { setQty(getQty() + 1); });
    if (stickyMinus) stickyMinus.addEventListener('click', function () { setQty(getQty() - 1); });
    if (stickyPlus) stickyPlus.addEventListener('click', function () { setQty(getQty() + 1); });
    if (qtyInput) {
      qtyInput.addEventListener('change', function () {
        setQty(qtyInput.value);
      });
    }

    // Mirror inline ATC click from sticky bar (re-uses the global submit
    // interceptor in sm-card-product.js — no need to duplicate the
    // /cart/add fetch logic here).
    // GoKwik claims buy buttons by CLONING them (cloneNode + replaceChild
    // in gokwik.liquid's replaceButton), so node references captured at
    // init can go stale — the clone keeps our data- attributes but none of
    // our listeners. Re-query the live node at click time so the proxy
    // click lands on whatever is actually in the DOM (GoKwik's clone
    // included, which is exactly what routes the tap into their popup).
    // ── Preview-toolbar guard (id-agnostic) ─────────────────────────
    // Shopify's preview overlay is a full-viewport transparent iframe that
    // swallows taps meant for the sticky bar (its id varies: PBarNextFrame,
    // preview-bar-iframe, ...). Instead of chasing ids, hit-test the bottom
    // strip: if an iframe answers, clip its hit-area to its visible pill
    // and lift the sticky bar above it. Runs late (the toolbar injects
    // async); never matches on live where no overlay exists.
    function guardPreviewOverlay() {
      if (!stickyBar) return;
      var el = document.elementFromPoint(Math.floor(window.innerWidth / 2), window.innerHeight - 24);
      if (el && el.tagName === 'IFRAME') {
        el.style.setProperty('clip-path', 'inset(calc(100% - 72px) 0 0 0)', 'important');
        stickyBar.style.setProperty('bottom', '72px', 'important');
      }
    }
    setTimeout(guardPreviewOverlay, 2500);
    setTimeout(guardPreviewOverlay, 6000);

    if (stickyAtc && atcBtn) {
      stickyAtc.addEventListener('click', function () {
        var liveAtc = root.querySelector('[data-sm-pdp-atc]') || atcBtn;
        if (liveAtc.disabled) return;
        liveAtc.click();
      });
    }
    if (stickyBuyNow && buyNowBtn) {
      // Pre-mark the sticky twin as processed: GoKwik's attach() bails on
      // data-gokwik-processed, so it never clones this button and our
      // proxy listener survives. (Its visibility check passes for the
      // off-screen translated bar on mobile, so it WOULD otherwise be
      // claimed and this listener silently stripped.)
      stickyBuyNow.dataset.gokwikProcessed = 'true';

      // GoKwik's SDK takes a moment to open its popup and shows its own
      // "Processing..." only on the (off-screen) inline button, so the
      // sticky tap feels dead. Mirror a loading state on the sticky button
      // until the GoKwik iframe is actually visible, with a safety timeout.
      function gokwikPopupVisible() {
        var frames = document.querySelectorAll('iframe');
        for (var i = 0; i < frames.length; i++) {
          if (/gokwik/i.test(frames[i].src || '')) {
            var r = frames[i].getBoundingClientRect();
            if (r.width > 50 && r.height > 50) return true;
          }
        }
        return false;
      }

      var stickyBuyNowLabel = stickyBuyNow.querySelector('[data-sm-pdp-sticky-buy-now-label]');

      function stopStickyLoading(originalLabel) {
        stickyBuyNow.classList.remove('is-loading');
        stickyBuyNow.disabled = false;
        if (stickyBuyNowLabel && originalLabel) stickyBuyNowLabel.textContent = originalLabel;
      }

      stickyBuyNow.addEventListener('click', function () {
        if (stickyBuyNow.classList.contains('is-loading')) return;
        var liveBuyNow = root.querySelector('[data-sm-pdp-buy-now]') || buyNowBtn;
        if (liveBuyNow.disabled) return;

        var strings = window.smPdpStrings || {};
        var originalLabel = stickyBuyNowLabel ? stickyBuyNowLabel.textContent : null;
        stickyBuyNow.classList.add('is-loading');
        stickyBuyNow.disabled = true;
        if (stickyBuyNowLabel) stickyBuyNowLabel.textContent = strings.processing || 'Processing…';

        liveBuyNow.click();

        var waited = 0;
        var poll = setInterval(function () {
          waited += 200;
          if (gokwikPopupVisible() || waited >= 10000) {
            clearInterval(poll);
            stopStickyLoading(originalLabel);
          }
        }, 200);
      });
    }

    // Buy Now: post to /cart/add then redirect to /checkout.
    // The global submit listener in sm-card-product.js intercepts any
    // form with action containing /cart/add. We DON'T want that for
    // Buy Now (we want a checkout redirect, not a toast), so we fetch
    // directly here rather than dispatching submit on the form.
    if (buyNowBtn && form) {
      buyNowBtn.addEventListener('click', function () {
        if (buyNowBtn.disabled) return;
        var variantId = variantInput ? variantInput.value : null;
        if (!variantId) return;

        var strings = window.smPdpStrings || {};
        var originalLabel = buyNowLabel ? buyNowLabel.textContent : (strings.buyNow || 'Buy Now');
        buyNowBtn.disabled = true;
        // Mirror the disabled state to the sticky-bar twin so a user
        // tapping the bottom CTA cannot double-fire the fetch.
        if (stickyBuyNow) stickyBuyNow.disabled = true;
        if (buyNowLabel) buyNowLabel.textContent = strings.processing || 'Processing…';

        var fd = new FormData();
        fd.append('id', variantId);
        fd.append('quantity', String(getQty()));

        fetch((window.routes && window.routes.cart_add_url) || '/cart/add.js', {
          method: 'POST',
          headers: { 'X-Requested-With': 'XMLHttpRequest', Accept: 'application/javascript' },
          body: fd,
        })
          .then(function (r) { return r.json().then(function (b) { return { ok: r.ok, body: b }; }); })
          .then(function (res) {
            if (!res.ok || res.body.status) {
              throw new Error((res.body && (res.body.description || res.body.message)) || 'Could not add to cart');
            }
            window.location.href = '/checkout';
          })
          .catch(function (err) {
            console.warn('[sm-pdp] Buy Now failed:', err && err.message);
            buyNowBtn.disabled = false;
            if (stickyBuyNow) stickyBuyNow.disabled = false;
            if (buyNowLabel) buyNowLabel.textContent = strings.tryAgain || 'Try again';
            setTimeout(function () {
              if (buyNowLabel) buyNowLabel.textContent = originalLabel;
            }, 1600);
          });
      });
    }

    // Mobile sticky-bar visibility — only attach on mobile.
    // NOT an IntersectionObserver: IO only fires on threshold CROSSINGS,
    // and a fast fling can jump the CTA row from below-viewport to
    // above-viewport between rendered frames — intersection stays 0, no
    // callback, bar never shows. A rAF-throttled scroll check evaluates
    // the actual geometry at every scroll position instead.
    if (buyRow && stickyBar) {
      var mq = window.matchMedia(MOBILE_QUERY);
      var scrollHandler = null;

      function setStickyState(active) {
        if (active) {
          stickyBar.classList.add('is-sticky');
          stickyBar.setAttribute('aria-hidden', 'false');
          // Toggle the body class so CSS can reserve bottom padding without
          // a :has() selector (older iOS < 15.4 has no :has() support).
          document.body.classList.add('sm-pdp-sticky-active');
        } else {
          stickyBar.classList.remove('is-sticky');
          stickyBar.setAttribute('aria-hidden', 'true');
          document.body.classList.remove('sm-pdp-sticky-active');
        }
      }

      function evaluateSticky() {
        // Show only after the CTA row has scrolled off the TOP of the
        // viewport (the user moved PAST the buy buttons). On load the row
        // sits below the fold — bottom > 0 — and must not trigger.
        setStickyState(buyRow.getBoundingClientRect().bottom <= 0);
      }

      function attachObserver() {
        if (scrollHandler || !mq.matches) return;
        var ticking = false;
        scrollHandler = function () {
          if (ticking) return;
          ticking = true;
          requestAnimationFrame(function () {
            ticking = false;
            evaluateSticky();
          });
        };
        window.addEventListener('scroll', scrollHandler, { passive: true });
        window.addEventListener('resize', scrollHandler, { passive: true });
        evaluateSticky();
      }

      function detachObserver() {
        if (!scrollHandler) return;
        window.removeEventListener('scroll', scrollHandler);
        window.removeEventListener('resize', scrollHandler);
        scrollHandler = null;
        setStickyState(false);
      }

      if (mq.matches) attachObserver();
      if (typeof mq.addEventListener === 'function') {
        mq.addEventListener('change', function (e) {
          if (e.matches) attachObserver();
          else detachObserver();
        });
      } else if (typeof mq.addListener === 'function') {
        // Safari < 14 fallback
        mq.addListener(function (e) {
          if (e.matches) attachObserver();
          else detachObserver();
        });
      }
    }

    // Apply availability state (used on init + variantChange).
    function applyAvailability(variant) {
      if (!variant) return;
      var available = !!variant.available;

      if (variantInput && variant.id != null) variantInput.value = variant.id;

      if (atcBtn) atcBtn.disabled = !available;
      if (buyNowBtn) buyNowBtn.disabled = !available;
      if (stickyAtc) stickyAtc.disabled = !available;
      if (stickyBuyNow) stickyBuyNow.disabled = !available;

      var s = window.smPdpStrings || {};
      var addToCart = s.addToCart || 'Add To Cart';
      var soldOut = s.soldOut || 'Sold out';
      var buyNow = s.buyNow || 'Buy Now';

      if (atcLabel) atcLabel.textContent = available ? addToCart : soldOut;
      if (buyNowLabel) buyNowLabel.textContent = available ? buyNow : soldOut;
      // Sticky-bar ATC has a label span next to the cart icon — write to the
      // span so we don't blow away the inline SVG. Fall back to textContent
      // for older renders where the label span isn't present.
      if (stickyAtcLabel) stickyAtcLabel.textContent = available ? addToCart : soldOut;
      else if (stickyAtc) stickyAtc.textContent = available ? addToCart : soldOut;
      if (stickyBuyNow) stickyBuyNow.textContent = available ? buyNow : soldOut;

      if (oosMsg) {
        if (available) oosMsg.setAttribute('hidden', '');
        else oosMsg.removeAttribute('hidden');
      }

      if (hurryMsg) {
        var qty = typeof variant.inventory_quantity === 'number' ? variant.inventory_quantity : null;
        var mgmt = variant.inventory_management;
        var policy = variant.inventory_policy;
        var showHurry =
          available &&
          mgmt &&
          policy === 'deny' &&
          qty != null &&
          qty > 0 &&
          qty <= 10;
        if (showHurry) {
          hurryMsg.removeAttribute('hidden');
          if (hurryQty) hurryQty.textContent = qty;
        } else {
          hurryMsg.setAttribute('hidden', '');
        }
      }

      // Update qty input max based on inventory (when tracked + denied).
      if (qtyInput) {
        var invQty = typeof variant.inventory_quantity === 'number' ? variant.inventory_quantity : null;
        if (variant.inventory_management && variant.inventory_policy === 'deny' && invQty != null && invQty > 0) {
          qtyInput.setAttribute('max', String(invQty));
          maxQty = invQty;
          if (parseInt(qtyInput.value, 10) > invQty) setQty(invQty);
        } else {
          qtyInput.removeAttribute('max');
          maxQty = 99;
        }
      }
    }

    // Listen for variant-changed events from Dawn's main-product section.
    // PUB_SUB_EVENTS / subscribe come from assets/pubsub.js.
    // Cap retries at 20 (~8s) so we don't poll forever if pubsub.js fails.
    var subscribeRetries = 0;
    function subscribeVariant() {
      subscribeRetries++;
      if (typeof subscribe !== 'function' || typeof PUB_SUB_EVENTS === 'undefined') {
        if (subscribeRetries <= 20) {
          setTimeout(subscribeVariant, 400);
        }
        return;
      }
      if (PUB_SUB_EVENTS.variantChange) {
        subscribe(PUB_SUB_EVENTS.variantChange, function (event) {
          if (!event || !event.data || !event.data.variant) return;
          applyAvailability(event.data.variant);
        });
      }
    }
    subscribeVariant();

    // Fallback: also listen for the custom DOM event some legacy blocks fire.
    document.addEventListener('variant:change', function (e) {
      if (e && e.detail && e.detail.variant) applyAvailability(e.detail.variant);
    });

    // Media gallery: thumbnail-swap.
    initMediaGallery(root);

    // Variant swatches (when product has multiple variants).
    initSwatches(root, applyAvailability);
  }

  // Read the serialised product object from the `<script type="application/json">`
  // tag embedded next to the block. Returns null when missing/parse-failed.
  function readProduct(root) {
    var node = root.querySelector('script[data-sm-pdp-product]');
    if (!node) return null;
    try {
      return JSON.parse(node.textContent || node.innerText || '{}');
    } catch (err) {
      console.warn('[sm-pdp] product JSON parse failed', err);
      return null;
    }
  }

  function updatePriceRow(root, variant) {
    if (!variant) return;
    // querySelectorAll, not querySelector: there are now TWO price rows — the
    // main one and the mobile sticky bar's — and both carry [data-sm-pdp-price].
    // Updating only the first would leave a stale price sitting next to the
    // sticky Add to Cart button after a variant change.
    var priceRows = root.querySelectorAll('[data-sm-pdp-price]');
    for (var i = 0; i < priceRows.length; i++) {
      updateOnePriceRow(priceRows[i], variant);
    }
  }

  function updateOnePriceRow(priceRow, variant) {
    var compareEl = priceRow.querySelector('[data-sm-pdp-compare]');
    var amountEl = priceRow.querySelector('[data-sm-pdp-amount]');
    var discountEl = priceRow.querySelector('[data-sm-pdp-discount]');

    var price = typeof variant.price === 'number' ? variant.price : parseInt(variant.price, 10);
    var compare = typeof variant.compare_at_price === 'number'
      ? variant.compare_at_price
      : (variant.compare_at_price ? parseInt(variant.compare_at_price, 10) : null);

    if (amountEl) amountEl.textContent = formatMoney(price);

    var showCompare = compare && compare > price;
    if (compareEl) {
      if (showCompare) {
        compareEl.textContent = formatMoney(compare);
        compareEl.removeAttribute('hidden');
      } else {
        compareEl.setAttribute('hidden', '');
      }
    }
    if (discountEl) {
      if (showCompare) {
        var pct = Math.round(((compare - price) / compare) * 100);
        discountEl.textContent = pct + '% off';
        discountEl.removeAttribute('hidden');
      } else {
        discountEl.setAttribute('hidden', '');
      }
    }
  }

  function updateMediaForVariant(root, variant) {
    if (!variant || !variant.featured_image || !variant.featured_image.src) return;
    var wrapper = root.querySelector('[data-sm-pdp-media]');
    if (!wrapper) return;
    var main = wrapper.querySelector('[data-sm-pdp-media-main]');
    if (!main) return;
    var img = main.querySelector('img');
    var src = variant.featured_image.src;
    // Avis/Shopify often returns protocol-relative URLs — let them work as-is.
    if (img && img.tagName === 'IMG') {
      img.removeAttribute('srcset');
      img.removeAttribute('sizes');
      img.src = src;
      if (variant.featured_image.alt) img.alt = variant.featured_image.alt;
    }

    // Sync active thumb state. If a thumb exists for this media id, mark it
    // active; otherwise clear all active state so the user can still tap
    // back to gallery thumbs without a stale highlight.
    var thumbs = wrapper.querySelectorAll('[data-sm-pdp-media-thumb]');
    var matchId = variant.featured_image.id || variant.featured_media && variant.featured_media.id;
    var matched = false;
    thumbs.forEach(function (t) {
      var thumbId = t.getAttribute('data-media-id');
      if (matchId && String(thumbId) === String(matchId)) {
        t.classList.add('is-active');
        t.setAttribute('aria-current', 'true');
        matched = true;
      } else {
        t.classList.remove('is-active');
        t.removeAttribute('aria-current');
      }
    });
    // No matching thumb — clear all active state so legacy "first thumb"
    // highlight doesn't lie about which media is showing.
    if (!matched) {
      // already cleared
    }
  }

  function initSwatches(root, applyAvailability) {
    var swatchInputs = root.querySelectorAll('[data-sm-pdp-swatch-input]');
    if (!swatchInputs.length) return;
    var product = readProduct(root);
    if (!product || !Array.isArray(product.variants) || !Array.isArray(product.options)) return;
    var variantInput = root.querySelector('[data-sm-pdp-variant-id]');

    function selectedOptions() {
      var out = [];
      for (var i = 0; i < product.options.length; i++) {
        var checked = root.querySelector('[data-sm-pdp-swatch-input][data-option-index="' + i + '"]:checked');
        if (checked) out.push(checked.value);
      }
      return out;
    }

    function findVariant(opts) {
      return product.variants.find(function (v) {
        return opts.every(function (opt, index) {
          return v.options[index] === opt;
        });
      });
    }

    function handleChange(input) {
      var optionIndex = parseInt(input.dataset.optionIndex, 10);
      var span = root.querySelector('[data-selected-value-' + optionIndex + ']');
      if (span) span.textContent = input.value;

      var variant = findVariant(selectedOptions());
      if (!variant) return;

      if (variantInput) variantInput.value = variant.id;
      updatePriceRow(root, variant);
      updateMediaForVariant(root, variant);
      if (typeof applyAvailability === 'function') applyAvailability(variant);

      // Notify other blocks (Dawn convention) — guard against missing pubsub.
      try {
        if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined' && PUB_SUB_EVENTS.variantChange) {
          publish(PUB_SUB_EVENTS.variantChange, { source: 'sm-pdp', data: { variant: variant } });
        }
      } catch (err) { /* no-op */ }

      // Fallback DOM event for non-pubsub listeners.
      document.dispatchEvent(new CustomEvent('variant:change', { detail: { variant: variant } }));
    }

    swatchInputs.forEach(function (input) {
      input.addEventListener('change', function () { handleChange(input); });
    });
  }

  // Avis Plus pill-button label — set the CSS custom property `--bundle-text`
  // on every `.avis-btn-open-popup` so the ::after overlay reads the right
  // copy ("Build Your Own Custom Jar Now" vs "Kit Now"). Avis injects the
  // button asynchronously, so we run the function on init, again at 1s, and
  // observe the body for mutations — same shape as the legacy IIFE.
  function initAvisBundleText() {
    var path = window.location.pathname;
    var handle = '';
    var match = path.match(/\/products\/([^\/?#]+)/);
    if (match) handle = match[1];

    function getText() {
      if (handle && BUNDLE_TEXT_MAP[handle]) return BUNDLE_TEXT_MAP[handle];
      return 'Build Your Own Custom Kit Now';
    }

    // Allowlist of safe characters for the pill label. The label is injected
    // into a CSS `content:` declaration via a custom property, so any quote
    // or backslash that survives could break out of the string. Stripping
    // everything outside [A-Za-z0-9 space - ' ! ?] is a tighter guarantee
    // than just escaping double quotes: the map is theme-controlled, but
    // future edits (or a misconfigured Avis handle map) can't introduce
    // odd characters that CSS would mis-parse.
    function sanitiseLabel(text) {
      return String(text).replace(/[^a-zA-Z0-9 \-'!?]/g, '');
    }

    function applyText() {
      var text = sanitiseLabel(getText());
      var nodes = document.querySelectorAll('.avis-btn-open-popup');
      nodes.forEach(function (el) {
        el.style.setProperty('--bundle-text', '"' + text + '"');
      });
      return nodes.length;
    }

    // Gate: only run on personalised PDPs. Either the body class is set,
    // or the URL path matches a known personalised handle. Belt-and-braces
    // because `personalised-product` is added in layout/theme.liquid only
    // when the handle list contains the current handle.
    var bodyMatches = document.body && document.body.classList && document.body.classList.contains('personalised-product');
    if (!bodyMatches && !BUNDLE_TEXT_MAP[handle]) return;

    applyText();
    setTimeout(applyText, 1000);

    // Cap the MutationObserver: bail after we successfully labelled at least
    // one button, or after 20 mutation batches (~8s in practice, even on a
    // chatty page). Prevents the observer from running forever on every DOM
    // change for the life of the session.
    var attempts = 0;
    var observer = new MutationObserver(function () {
      attempts += 1;
      var found = applyText();
      if (found > 0 || attempts >= 20) {
        observer.disconnect();
      }
    });
    observer.observe(document.body, { childList: true, subtree: true });
  }

  function initMediaGallery(root) {
    var wrapper = root.querySelector('[data-sm-pdp-media]');
    if (!wrapper) return;

    var main = wrapper.querySelector('[data-sm-pdp-media-main]');
    var thumbs = wrapper.querySelector('[data-sm-pdp-media-thumbs]');
    if (!main || !thumbs) return;

    var media;
    try {
      media = JSON.parse(wrapper.getAttribute('data-sm-pdp-media') || '[]');
    } catch (err) {
      console.warn('[sm-pdp] media JSON parse failed', err);
      return;
    }
    if (!Array.isArray(media) || media.length < 2) return;

    var byId = {};
    media.forEach(function (m) { byId[String(m.id)] = m; });

    function renderMain(m) {
      if (!m) return;
      // Prefer in-place src swap so we keep the same <img> node (browser
      // can re-use the decoded bitmap when revisiting).
      var existing = main.querySelector('img');
      if (existing && existing.tagName === 'IMG') {
        existing.removeAttribute('srcset');
        existing.removeAttribute('sizes');
        existing.src = m.url || '';
        if (m.alt) existing.alt = m.alt;
        existing.loading = 'eager';
        return;
      }
      // Initial main was a <video>/<iframe>; replace with a fresh <img>
      // via DOM APIs (no innerHTML — values are Liquid-JSON-serialised
      // but we keep them out of string templates to satisfy XSS-strict
      // tooling). Full video swap would need <source>/<iframe> metadata
      // we don't serialise — out of scope for v1.
      while (main.firstChild) main.removeChild(main.firstChild);
      var img = document.createElement('img');
      img.className = 'sm-pdp__media-img';
      img.src = m.url || '';
      img.alt = m.alt || '';
      img.loading = 'eager';
      main.appendChild(img);
    }

    function activate(thumb) {
      var id = thumb.getAttribute('data-media-id');
      if (!id) return;
      var m = byId[String(id)];
      if (!m) return;
      thumbs.querySelectorAll('[data-sm-pdp-media-thumb].is-active').forEach(function (el) {
        el.classList.remove('is-active');
        el.removeAttribute('aria-current');
      });
      thumb.classList.add('is-active');
      thumb.setAttribute('aria-current', 'true');
      renderMain(m);
    }

    thumbs.addEventListener('click', function (e) {
      var t = e.target.closest('[data-sm-pdp-media-thumb]');
      if (!t || !thumbs.contains(t)) return;
      activate(t);
    });

    thumbs.addEventListener('keydown', function (e) {
      if (e.key !== 'Enter' && e.key !== ' ') return;
      var t = e.target.closest('[data-sm-pdp-media-thumb]');
      if (!t || !thumbs.contains(t)) return;
      // <button> natively activates on Enter/Space — only intercept for
      // non-button thumb elements (defensive).
      if (t.tagName !== 'BUTTON') {
        e.preventDefault();
        activate(t);
      }
    });
  }

  /*
   * Infinite-marquee trust-icons row on mobile.
   *
   * Approach: duplicate the items once so the track is 2× wide, then
   * continuously increment scrollLeft via requestAnimationFrame. When
   * scrollLeft reaches the original-set width, snap back to 0 instantly —
   * because the duplicate set is identical, the snap is invisible.
   *
   * - Only active on viewports where the row would overflow (≤989px).
   * - Pauses while the user is interacting (touch / pointer / wheel) and
   *   resumes 1.5s after the last interaction.
   * - Pauses when off-screen via IntersectionObserver so it doesn't
   *   churn CPU on long pages.
   * - The clones are aria-hidden so screen readers don't repeat icons.
   */
  function initTrustAutoScroll(root) {
    var ul = root.querySelector('.sm-pdp__trust');
    if (!ul || ul.dataset.smTrustAutoBound === '1') return;
    ul.dataset.smTrustAutoBound = '1';

    var SPEED_PX_PER_SEC = 70;
    var RESUME_DELAY_MS = 1500;
    var rafId = null;
    var lastTs = 0;
    var paused = false;
    var visible = true;
    var resumeTimer = null;
    var originalSetWidth = 0;
    var cloned = false;

    function isMarqueeable() {
      // Skip on desktop layouts (where the row uses CSS Grid and doesn't overflow).
      return getComputedStyle(ul).overflowX !== 'visible' && ul.children.length > 0;
    }

    function clone() {
      if (cloned) return;
      var originals = Array.from(ul.children);
      originals.forEach(function (li) {
        var c = li.cloneNode(true);
        c.setAttribute('aria-hidden', 'true');
        c.classList.add('sm-pdp__trust-item--clone');
        ul.appendChild(c);
      });
      cloned = true;
      // Measure width of the ORIGINAL set (= half the total scrollWidth).
      // This is our wrap-around point.
      originalSetWidth = ul.scrollWidth / 2;
    }

    function tick(ts) {
      if (!visible || paused) { rafId = null; return; }
      if (!lastTs) lastTs = ts;
      var dt = (ts - lastTs) / 1000;
      lastTs = ts;
      var dx = SPEED_PX_PER_SEC * dt;
      ul.scrollLeft += dx;
      if (ul.scrollLeft >= originalSetWidth) {
        ul.scrollLeft -= originalSetWidth;
      }
      rafId = requestAnimationFrame(tick);
    }

    function start() {
      if (!isMarqueeable() || paused || !visible) return;
      if (!cloned) clone();
      if (!originalSetWidth) return;
      if (rafId) return;
      lastTs = 0;
      rafId = requestAnimationFrame(tick);
    }
    function stop() {
      if (rafId) { cancelAnimationFrame(rafId); rafId = null; }
      lastTs = 0;
    }
    function pauseAndScheduleResume() {
      paused = true;
      stop();
      if (resumeTimer) clearTimeout(resumeTimer);
      resumeTimer = setTimeout(function () {
        paused = false;
        start();
      }, RESUME_DELAY_MS);
    }

    ['touchstart', 'pointerdown', 'wheel'].forEach(function (evt) {
      ul.addEventListener(evt, pauseAndScheduleResume, { passive: true });
    });

    var io = new IntersectionObserver(function (entries) {
      visible = entries[0].isIntersecting;
      if (visible) start(); else stop();
    }, { threshold: 0 });
    io.observe(ul);
    smPdpObservers.add(io);

    // Recompute clone width on resize (orientation change / DPR shifts).
    window.addEventListener('resize', function () {
      if (cloned) originalSetWidth = ul.scrollWidth / 2;
    });

    start();
  }

  function init() {
    document.querySelectorAll('[data-sm-pdp]').forEach(function (root) {
      initBlock(root);
      initTrustAutoScroll(root);
    });
    initAvisBundleText();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
  // Re-scan if the theme editor injects the block live.
  document.addEventListener('shopify:section:load', init);
  document.addEventListener('shopify:block:select', init);

  // Theme editor section-unload cleanup. Without this, the
  // `sm-pdp-sticky-active` body class can linger across reloads and starve
  // the page of bottom padding, AND IntersectionObservers from the prior
  // mount keep firing against stale DOM nodes.
  document.addEventListener('shopify:section:unload', function () {
    document.body.classList.remove('sm-pdp-sticky-active');
    smPdpObservers.forEach(function (obs) {
      try { obs.disconnect(); } catch (e) { /* no-op */ }
    });
    smPdpObservers.clear();
  });

  // ─── PDP Available Offers — copy code to clipboard ─────────────

  /* The visual "Copied!" swap inside the PDP button is invisible to assistive
     tech: a button's descendants are presentational in ARIA, so nothing in the
     subtree gets its own accessible object. The only thing a screen reader
     reports is the button's accessible name, so swap that instead and put it
     back with the class. Cart-drawer chips carry no data-sm-copied-label, so
     they keep their existing behaviour untouched. */
  function markCopied(btn) {
    btn.classList.add('is-copied');
    var copiedLabel = btn.getAttribute('data-sm-copied-label');
    if (copiedLabel && !btn.hasAttribute('data-sm-label-was')) {
      btn.setAttribute('data-sm-label-was', btn.getAttribute('aria-label') || '');
      btn.setAttribute('aria-label', copiedLabel);
    }
    setTimeout(function() {
      btn.classList.remove('is-copied');
      if (btn.hasAttribute('data-sm-label-was')) {
        btn.setAttribute('aria-label', btn.getAttribute('data-sm-label-was'));
        btn.removeAttribute('data-sm-label-was');
      }
    }, 1500);
  }

  document.addEventListener('click', function(e) {
    var btn = e.target.closest('[data-sm-pdp-offer-copy]');
    if (!btn) return;
    var code = btn.getAttribute('data-sm-pdp-offer-copy');
    if (!code) return;
    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(code).then(function() {
        markCopied(btn);
      }).catch(function() {
        // Fallback: select+copy via temporary input
        var tmp = document.createElement('input');
        tmp.value = code;
        document.body.appendChild(tmp);
        tmp.select();
        try { document.execCommand('copy'); } catch (err) {}
        tmp.remove();
        markCopied(btn);
      });
    }
  });
})();

/* ─── Shiprocket Smart EDD widget skin ───────────────────────────────
   The widget renders in an OPEN shadow root on #delivery-widget, so page
   CSS can't reach it. Inject a style tag into the shadow root instead.
   Fonts declared here resolve against document-level @font-face (Poppins/
   Inter are loaded by component-sm-pdp.css). */
(function () {
  var CSS = [
    '.sr-delivery-widget.sr-delivery-widget.sr-delivery-widget, .sr-delivery-container.sr-delivery-container {',
    '  background: transparent !important; box-shadow: none !important;',
    '  border: none !important; border-radius: 0 !important;',
    '  max-width: 100% !important; width: 100% !important;',
    '  margin: 0 !important; padding: 0 !important;',
    '}',
    '.sr-title {',
    "  font-family: 'Poppins', sans-serif !important;",
    '  font-size: 16px !important; font-weight: 600 !important;',
    '  color: #1A1A1A !important; margin: 0 0 12px !important;',
    '}',
    '.sr-delivery-widget, .sr-delivery-widget * {',
    "  font-family: 'Poppins', sans-serif !important;",
    '}',
    '.sr-pincode-input {',
    '  font-size: 15px !important; color: #1A1A1A !important;',
    '  background: transparent !important;',
    '}',
    '.sr-delivery-widget button, .sr-delivery-widget .sr-check {',
    '  font-size: 14px !important; font-weight: 600 !important;',
    '  color: #1E2A33 !important; cursor: pointer;',
    '}'
  ].join('\n');
  var tries = 0;
  var timer = setInterval(function () {
    tries++;
    var host = document.getElementById('delivery-widget');
    var root = host && host.shadowRoot;
    if (root) {
      if (!root.getElementById || !root.querySelector('#sm-edd-skin')) {
        var style = document.createElement('style');
        style.id = 'sm-edd-skin';
        style.textContent = CSS;
        root.appendChild(style);
      }
      clearInterval(timer);
    } else if (tries > 40) {
      clearInterval(timer);
    }
  }, 250);
})();

/* ─── Judge.me average-rating reformat (task 76, 2026-07-02) ───────────
   Judge.me renders the summary average as a single text node, e.g.
   "4.82 out of 5", inside `.jdgm-rev-widg__summary-average`. Figma wants
   only the leading number shown, at 48px bold, with "out of 5" dropped.
   Judge.me mounts asynchronously (its own JS fetches + renders review
   data), so poll for the node like the Shiprocket shadow-root skin above.
   Idempotent: bails if `.sm-pdp-jdgm-avg-num` is already present so a
   second run (e.g. Judge.me re-rendering after a sort change) is a no-op
   rather than double-wrapping. Guarded end-to-end so it can never throw
   on non-PDP pages where `#judgeme_product_reviews` doesn't exist. */
(function () {
  try {
    if (!document.getElementById('judgeme_product_reviews')) return;
    var tries = 0;
    var timer = setInterval(function () {
      tries++;
      try {
        var avg = document.querySelector('.sm-pdp__reviews-section .jdgm-rev-widg__summary-average');
        if (avg) {
          if (!avg.querySelector('.sm-pdp-jdgm-avg-num')) {
            var match = (avg.textContent || '').match(/[\d.]+/);
            if (match) {
              avg.innerHTML = '<span class="sm-pdp-jdgm-avg-num">' + match[0] + '</span>';
            }
          }
          clearInterval(timer);
        } else if (tries > 40) {
          clearInterval(timer);
        }
      } catch (innerErr) {
        clearInterval(timer);
      }
    }, 250);
  } catch (err) {
    /* never throw on non-PDP pages */
  }
})();

/* ------------------------------------------------------------------
 * Available Offers carousel (PDP only).
 *
 * Deliberately manual: the cart drawer autoplays its offers marquee, but a
 * product page shouldn't have cards sliding past while someone is reading, so
 * here it's paged by chevrons and swipe.
 *
 * The track is resolved from the DOM on every click rather than captured in a
 * closure — the strip can be re-rendered, and a stale reference would leave
 * the buttons scrolling a detached element.
 * ------------------------------------------------------------------ */
(function () {
  function track(scope) {
    return scope ? scope.querySelector('[data-sm-offers-track]') : null;
  }

  function step(t) {
    var card = t && t.firstElementChild ? t.firstElementChild.firstElementChild : null;
    if (!card) return 0;
    var styles = window.getComputedStyle(t);
    var gap = parseFloat(styles.columnGap || styles.gap) || 0;
    return card.offsetWidth + gap;
  }

  function sync(scope) {
    var t = track(scope);
    if (!t) return;
    var prev = scope.querySelector('[data-sm-offers-prev]');
    var next = scope.querySelector('[data-sm-offers-next]');
    var max = t.scrollWidth - t.clientWidth;
    if (prev) prev.disabled = t.scrollLeft <= 2;
    if (next) next.disabled = t.scrollLeft >= max - 2;
  }

  document.addEventListener('click', function (event) {
    var btn = event.target.closest('[data-sm-offers-prev], [data-sm-offers-next]');
    if (!btn) return;
    var scope = btn.closest('[data-sm-offers]');
    var t = track(scope);
    if (!t) return;
    event.preventDefault();
    var dir = btn.hasAttribute('data-sm-offers-next') ? 1 : -1;
    t.scrollBy({ left: dir * (step(t) || t.clientWidth), behavior: 'smooth' });
    setTimeout(function () {
      sync(scope);
    }, 350);
  });

  function init() {
    document.querySelectorAll('.sm-offers--pdp[data-sm-offers]').forEach(function (scope) {
      var t = track(scope);
      if (!t || t.__smOffersBound) return;
      t.__smOffersBound = true;
      t.addEventListener('scroll', function () { sync(scope); }, { passive: true });
      sync(scope);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
  window.addEventListener('resize', init);
})();
